import React, { useState } from 'react';
import {
  Alert, Button, Stack, TextField, Typography,
} from '@mui/material';
import { useOperations } from '../operations/OperationsProvider';
import { challanOwed, CHALLAN_LABELS, realShipment } from './challan';

// The paper catching up with a lorry that is already standing in the parking.
//
// This is the half of the parking counter that happens later. The lorry was written down on its
// number plate so it could be parked and counted; when the driver produces the challan, or the
// office sends it over, it is typed here and the load goes into the plant with nothing left to do
// at the gate.
//
// Only what is outstanding is asked for, and a desk may enter part of it: paper arrives in pieces,
// and a counter that refuses the shipment number until the consignee is also known is a counter
// that gets a number written on the back of somebody's hand instead.
const ChallanPanel = ({ trip }) => {
  const { mutate } = useOperations();
  const owed = challanOwed(trip);
  const [form, setForm] = useState({});
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  if (!owed.length) return null;
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const entered = owed.filter((key) => String(form[key] ?? '').trim());
  const save = async (event) => {
    event.preventDefault(); setPending(true); setError(null); setDone(null);
    try {
      await mutate(`trips/${trip.id}/challan`, Object.fromEntries(
        entered.map((key) => [key, String(form[key]).trim()]),
      ));
      setDone(entered.length === owed.length
        ? 'The challan is on the trip. This load can be recorded into the plant.'
        : `${entered.map((key) => CHALLAN_LABELS[key]).join(', ')} saved. The rest can follow.`);
      setForm({});
    } catch (err) { setError(err.message); } finally { setPending(false); }
  };
  return (
    <Stack component="form" onSubmit={save} gap={1.5}>
      {error && <Alert severity="error">{error}</Alert>}
      {done && <Alert severity="success">{done}</Alert>}
      <Typography variant="body2" color="text.secondary">
        This truck was taken into the parking on its number plate. The plant gate will not take it in
        until the challan is entered — here, or at the gate itself.
      </Typography>
      {owed.map((key) => (
        <TextField
          key={key}
          fullWidth
          disabled={pending}
          label={CHALLAN_LABELS[key]}
          value={form[key] || ''}
          onChange={(e) => set(key, e.target.value)}
          inputProps={{ maxLength: 500 }}
          helperText={key === 'shipmentId' && !realShipment(trip)
            ? 'The number the load was issued under; the plant and the ERP agree on this one'
            : undefined}
        />
      ))}
      <Stack direction="row" gap={1}>
        <Button type="submit" variant="contained" disabled={pending || !entered.length}>
          {pending ? 'Saving…' : entered.length && entered.length < owed.length ? 'Save what is in hand' : 'Save challan details'}
        </Button>
      </Stack>
    </Stack>
  );
};

export default ChallanPanel;
